import React, { useCallback, useEffect, useRef, useState } from 'react'
import style from './NikeVide.module.scss'
import { IEcommerceAnswerSchema, IEcommerceVideoAsset } from '../types'
import useTimeline from '../../../modules/timeline/useTimeline'

interface INikeVideoProps {
  asset: IEcommerceVideoAsset
  answer: IEcommerceAnswerSchema
  assetIndex: number
}
const NikeVideo = ({ asset, answer, assetIndex }: INikeVideoProps) => {
  // custom hooks calls
  const timeline = useTimeline()

  // state
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [showControls, setShowControls] = useState<boolean>(true)

  // refs
  const videoRef = useRef<HTMLVideoElement>(null)

  // functions
  const togglePlayPause = useCallback(async () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        await videoRef.current.play()
      } else {
        videoRef.current.pause()
      }
      setShowControls(true)
      if (!isPlaying) {
        setTimeout(() => {
          setShowControls(false)
        }, 2000)
      }
    }
  }, [isPlaying])
  // The <video> can unmount mid-play (slide leaves the carousel), so its own pause event
  // never reaches React. Ref-based and idempotent: a delivered pause clears the ref.
  const playingRef = useRef<boolean>(false)

  const logVideoEvent = (type: 'play_video' | 'pause_video') =>
    timeline.log({ ref: answer.id, target: assetIndex.toString(), type })

  const onVideoPlay = () => {
    playingRef.current = true
    setIsPlaying(true)
    logVideoEvent('play_video')
  }

  const onVideoPause = () => {
    playingRef.current = false
    setIsPlaying(false)
    logVideoEvent('pause_video')
  }

  const onVideoEnded = useCallback(() => {
    setIsPlaying(false)
  }, [])

  const onLoadedData = useCallback(() => {
    if (videoRef.current) {
      setIsPlaying(!videoRef.current.paused)
    }
  }, [])

  // effects
  useEffect(
    () => () => {
      if (playingRef.current) logVideoEvent('pause_video')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <div className={style.nikeVideo} onClick={() => void togglePlayPause()}>
      <video
        ref={videoRef}
        className={style.video}
        src={asset.video.url}
        playsInline
        muted
        loop
        controls={false}
        onPlay={onVideoPlay}
        onPause={onVideoPause}
        onEnded={onVideoEnded}
        onLoadedData={onLoadedData}
      />
      {showControls && (
        <button className={style.videoControls}>
          <div className={isPlaying ? style.pause : style.play} />
        </button>
      )}
    </div>
  )
}

export default NikeVideo
